import { beginLoading, endLoading } from "@app/AppLayout/LoadingContext";
import { useNotification } from '@app/AppLayout/NotificationContext';
import { DetailPageHeader } from '@app/components/DetailPageHeader';
import InlineEdit from '@app/components/InLineEdit';
import { COURSE_STATUS, Course } from '@app/model/Course';
import { ddMMyyyyFormat, getBoolean } from '@app/utils/functions';
import { ActionList, ActionListItem, Alert, AlertVariant, Button, Card, DescriptionList, DescriptionListGroup, FlexItem, FormAlert, Icon, Label, MenuToggle, MenuToggleElement, Modal, ModalBody, ModalFooter, ModalHeader, ModalVariant, PageSection, Pagination, PaginationVariant, Select, SelectOption, SplitItem, Title, Toolbar, ToolbarContent, ToolbarGroup, ToolbarItem, Tooltip } from '@patternfly/react-core';
import { ExclamationTriangleIcon, FilterIcon, PencilAltIcon } from '@patternfly/react-icons';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';

import { CourseVersionApi } from '@app/api';
import { NotFoundState } from '@app/components/NotFoundState';
import { EntityChangeLog } from '@app/components/EntityChangeLog';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { RemoveButton } from '@app/components/RemoveButton';
import { isNotFound } from '@app/utils/ApiError';
import { useBreadcrumbLabel } from '@app/hooks/useBreadcrumbLabel';
import { useEntity } from '@app/hooks/useEntity';
import { Curriculum } from '@app/model/Curriculum';
import { CourseVersion } from '@app/model/CourseVersion';
import { loadCertifications, loadCourseBySku } from '@app/store/entityLookups';
import { useCourseStore, useCurriculumStore } from '@app/store/entityStore';
import { hasRealmRole } from '@app/utils/keycloak';
import { ROLE } from '@app/utils/roles';
import * as React from 'react';
import { useBlocker, useNavigate, useParams } from 'react-router-dom';
import { CourseVersionModal } from './CourseVersionModal';

type ValidateStatus = 'error' | 'default';

/** Nothing is marked until a save has been attempted - see validateCourse. */
const pristineValidation: Record<string, ValidateStatus> = {
  sku: 'default', name: 'default', certification: 'default', bundleCourse: 'default'
};

/** What is missing from the record, field by field, so the form can mark each one itself. */
const validateCourse = (c: Course): Record<string, ValidateStatus> => ({
  sku: c.sku?.trim() ? 'default' : 'error',
  name: c.name?.trim() ? 'default' : 'error',
  // A bundled SKU is a course and an exam sold together, so one that says it is a bundle is
  // incomplete until it names both halves.
  certification: !c.isBundle || c.certification ? 'default' : 'error',
  bundleCourse: !c.isBundle || c.bundleCourse ? 'default' : 'error'
});

const VALIDATION_MESSAGE: Record<string, string> = {
  sku: 'SKU is required.',
  name: 'Course name is required.',
  certification: 'Required for a bundle: this is its exam half.',
  bundleCourse: 'Required for a bundle: this is its course half.'
};

/** The blank record the create form starts from. Status is seeded so the select opens on a
 * real option rather than on an empty one the user has to notice and fill in. */
const blankCourse = (): Course => ({
  status: COURSE_STATUS.ACTIVE,
  isCertification: false,
  isBundle: false,
  versions: []
} as unknown as Course);

interface CourseDetailProps {
  /**
   * The create form rather than a course.
   *
   * Set by the route: /courses/new is a static path - it has to be, so that it can be guarded
   * without guarding the detail page beside it - and a static path carries no :sku to read
   * 'new' out of. The segment is still recognised as a fallback, for anything reaching this
   * page through /courses/:sku.
   */
  isNew?: boolean;
}

const CourseDetail: React.FunctionComponent<CourseDetailProps> = ({ isNew: isCreateRoute = false }) => {
  const { sku } = useParams();
  const navigate = useNavigate();
  const { addNotification } = useNotification();

  const isNew = isCreateRoute || sku === 'new';

  /**
   * A course and its versions are what every class is scheduled against, so they are read by
   * everybody and written by an administrator alone. Only decides what to offer: the service
   * enforces it on each endpoint regardless - see README-permissions.md.
   */
  const isAdmin = hasRealmRole(ROLE.ADMIN);

  const [isSelectOpen, setIsSelectOpen] = React.useState<boolean>(false);
  const [changeLogOpen, setChangeLogOpen] = React.useState<boolean>(false);
  const [page, setPage] = React.useState<number>(1);
  const [perPage, setPerPage] = React.useState<number>(5);

  const [course, setCourse] = React.useState<Course>(isNew ? blankCourse() : {} as Course);
  const [originalCourse, setOriginalCourse] = React.useState<Course>(isNew ? blankCourse() : {} as Course);
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  const [validated, setValidated] = React.useState<Record<string, ValidateStatus>>(pristineValidation);
  /** Nothing is marked red before the user has asked for the record to be saved. */
  const [saveAttempted, setSaveAttempted] = React.useState<boolean>(false);

  /**
   * The fields whose pencil is currently open, by field id - see InlineEdit's onEditingChange.
   *
   * A pencil that is open holds a value the field has not been given yet: InlineEdit keeps what is
   * typed in a buffer of its own and only hands it over when the tick is clicked. Saving the record
   * from under it would write everything EXCEPT what the user is in the middle of typing, and would
   * look from the outside like the save had simply dropped a field - so Save is held shut until the
   * edit is either confirmed or thrown away.
   */
  const [openEditors, setOpenEditors] = React.useState<ReadonlySet<string>>(() => new Set());

  /**
   * Returns the set unchanged when a report says nothing new, which keeps the identity stable and
   * stops a re-render - InlineEdit re-reports on every render it is given a fresh callback on.
   */
  const onFieldEditingChange = React.useCallback((isEditing: boolean, field: string) => {
    setOpenEditors(current => {
      if (isEditing === current.has(field)) return current;
      const next = new Set(current);
      if (isEditing) next.add(field); else next.delete(field);
      return next;
    });
  }, []);

  const hasOpenEditor = openEditors.size > 0;

  const [isVersionModalOpen, setIsVersionModalOpen] = React.useState<boolean>(false);
  /** The version the modal is open over, or null when it is open to create one. */
  const [editedVersion, setEditedVersion] = React.useState<CourseVersion | null>(null);

  const [certifications, setCertifications] = React.useState<Course[]>([]);
  /** No course carries that SKU, so the page says so in place of the record. */
  const [notFound, setNotFound] = React.useState<boolean>(false);
  const { items: curricula } = useEntity<Curriculum>(useCurriculumStore);
  /** Every course, which the bundle select narrows down - see bundleCourses. */
  const { items: allCourses } = useEntity<Course>(useCourseStore);

  const saveCourse = useCourseStore(state => state.save);
  const removeCourse = useCourseStore(state => state.delete);
  const syncCourse = useCourseStore(state => state.syncItem);

  useBreadcrumbLabel(isNew ? 'New' : course.sku?.toUpperCase());

  // --- Derived State (Memoized Pagination) ---
  const versions = course.versions || [];
  const paginatedRows = React.useMemo(() => {
    const startIdx = (page - 1) * perPage;
    return versions.slice(startIdx, startIdx + perPage);
  }, [versions, page, perPage]);

  /**
   * What a bundle may bundle: a course, never an exam - the exam half of the SKU goes in the
   * Certification field - and never the bundle itself.
   */
  const bundleCourses = React.useMemo(
    () => allCourses.filter(c => !c.isCertification && c.id !== course.id),
    [allCourses, course.id]
  );

  // --- Effects ---
 React.useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      // The create form has no SKU in the address at all, and nothing to fetch by one
      if (!sku && !isNew) return;

      setNotFound(false);
      beginLoading();
      try {
        // A course being created has nothing to fetch, but the certification list is needed
        // either way — it fills the select on both the create form and the edit form.
        const [courseData, certsData] = await Promise.all([
          isNew ? Promise.resolve(blankCourse()) : loadCourseBySku(sku!),
          loadCertifications()
        ]);

        if (isMounted) {
          setCourse(courseData);
          setOriginalCourse(courseData);
          setCertifications(certsData);
          setIsDirty(false);
          setValidated(pristineValidation);
          setSaveAttempted(false);
        }
      } catch (error: any) {
        if (!isMounted) return;
        // A SKU that matches no course is the answer rather than a failure: the page renders it.
        if (isNotFound(error)) {
          setNotFound(true);
          return;
        }
        addNotification(error.message || 'Failed to load course data', AlertVariant.danger);
      } finally {
        endLoading();
      }
    };

    loadData();
    return () => { isMounted = false; };
  }, [sku, isNew]); // <-- Look how clean this dependency array is now!

  // --- Handlers ---
  const updateCourse = (updatedCourse: Course) => {
    setCourse(updatedCourse);
    setIsDirty(true);
    // Marks already on screen clear as the record is corrected. Before the first save attempt
    // nothing is marked at all: a form is not wrong for being half filled in.
    if (saveAttempted) setValidated(validateCourse(updatedCourse));
  };

  const onCancel = () => {
    setCourse(originalCourse);
    setIsDirty(false);
    setValidated(pristineValidation);
    setSaveAttempted(false);
  };

  const onSave = async () =>  {
    // What is wrong is said on the fields themselves rather than in a toast that fades while
    // the user is still looking for which one it meant.
    const result = validateCourse(course);
    setValidated(result);
    setSaveAttempted(true);
    if (Object.values(result).includes('error')) return;

    beginLoading();
    try {
      // 1. The store handles the POST/PUT request, the ingestion, AND the cache update!
      const freshCourse = await saveCourse({ ...course, sku: course.sku.trim(), name: course.name.trim() }, isNew);

      // 2. Update local component state with the ingested response
      setCourse(freshCourse);
      setOriginalCourse(freshCourse);
      setIsDirty(false);
      setValidated(pristineValidation);
      setSaveAttempted(false);

      addNotification(`Course ${isNew ? 'created' : 'saved'} with success!`, AlertVariant.success);

      // 3. This page is keyed by SKU, so a course that has just been created has to move off
      // /courses/new and onto its own URL, or a refresh would land back on an empty form.
      if (isNew) {
        isNavigatingDeliberately.current = true;
        navigate(`/courses/${freshCourse.sku}`, { replace: true });
      }
    } catch (error: any) {
      addNotification(error.message || "Failed to save course", AlertVariant.danger);
    } finally {
      endLoading();
    }
  };

  /** Defined here rather than inline so the tooltip below can wrap it without repeating it. */
  const saveButton = (
    <Button
      variant="primary"
      id="save-button"
      isDisabled={!isDirty || hasOpenEditor}
      onClick={onSave}
    >
      Save
    </Button>
  );

  /**
   * The browser's own warning, for leaving the application altogether - a closed tab, a reload, a
   * typed address. The text is the browser's and cannot be set; returnValue only asks for it.
   */
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = ''; // Required by modern browsers to show the native warning prompt
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  /**
   * Lets through the one navigation the page does of its own accord rather than the user doing it.
   *
   * THE CASE THIS EXISTS FOR is creating a course. onSave clears isDirty and then moves off
   * /courses/new, both in the same tick - so React has not re-rendered yet and the guard below is
   * still reading isDirty as true when the move goes past it. Without this the page would block its
   * own redirect and ask a user who had just saved whether they wanted to lose their changes.
   *
   * A ref and not state for the same reason: it has to be true for a navigation that happens before
   * the next render, and it is read back on the way through rather than rendered.
   */
  const isNavigatingDeliberately = React.useRef(false);

  /**
   * The same guard for navigation inside the application, which never unloads the page and so
   * never reaches beforeunload. Compared on pathname alone: this page puts its own paging and
   * filters on the query string, and a search that changed is not the user leaving.
   */
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (isNavigatingDeliberately.current) {
      isNavigatingDeliberately.current = false;
      return false;
    }
    return isDirty && currentLocation.pathname !== nextLocation.pathname;
  });

  // This page reads the course by SKU straight from the API, so a refetch is the refresh
  const onRefresh = async () => {
    if (!sku || isNew) return;
    const [courseData, certsData] = await Promise.all([
      loadCourseBySku(sku, true),
      loadCertifications(true)
    ]);
    setCourse(courseData);
    setOriginalCourse(courseData);
    setCertifications(certsData);
    setIsDirty(false);
    setValidated(pristineValidation);
    setSaveAttempted(false);
  };

  const onRemove = async () => {
    beginLoading();
    try {
      await removeCourse(course.id);
      addNotification("Course removed with success!", AlertVariant.success);
      // Same state the layout's Back button carries, so the list comes back filtered as it was.
      // Past the guard too: a record that has just been deleted has no changes left to lose.
      isNavigatingDeliberately.current = true;
      navigate('/courses', { state: { restoreFilters: true } });
    } catch (error: any) {
      addNotification(error.message || "Failed to remove course", AlertVariant.danger);
    } finally {
      endLoading();
    }
  };

  /**
   * Reads the versions back after one was written.
   *
   * Only the versions: a version is stored by its own endpoint, so the course form may well be
   * half edited when one is saved, and replacing the whole record here would throw those edits
   * away. syncItem keeps the Courses list in step - it shows each course's latest version,
   * which is exactly what a version write changes - and does nothing when the list is cold.
   */
  const reloadVersions = async () => {
    const fresh = await loadCourseBySku(sku!, true);
    setCourse(current => ({ ...current, versions: fresh.versions, latestVersion: fresh.latestVersion }));
    setOriginalCourse(current => ({ ...current, versions: fresh.versions, latestVersion: fresh.latestVersion }));
    // Removing the last row of the last page would otherwise leave the table on a page that
    // no longer exists, showing nothing at all.
    setPage(current => Math.min(current, Math.max(1, Math.ceil((fresh.versions?.length ?? 0) / perPage))));
    syncCourse(fresh);
  };

  const onNewVersion = () => {
    setEditedVersion(null);
    setIsVersionModalOpen(true);
  };

  const onEditVersion = (version: CourseVersion) => {
    setEditedVersion(version);
    setIsVersionModalOpen(true);
  };

  const onSaveVersion = async (payload: Partial<CourseVersion>) => {
    const isNewVersion = payload.id === undefined;
    beginLoading();
    try {
      if (isNewVersion) await CourseVersionApi.create(course.id, payload);
      else await CourseVersionApi.update(course.id, payload.id!, payload);

      await reloadVersions();
      addNotification(`Version ${isNewVersion ? 'created' : 'saved'} with success!`, AlertVariant.success);
      setIsVersionModalOpen(false);
      setEditedVersion(null);
    } catch (error: any) {
      // The modal stays open on a failure, so the draft that failed is still there to correct.
      addNotification(error.message || 'Failed to save the course version', AlertVariant.danger);
    } finally {
      endLoading();
    }
  };

  const onRemoveVersion = async (versionId: string | number) => {
    beginLoading();
    try {
      await CourseVersionApi.remove(course.id, versionId);
      await reloadVersions();
      addNotification('Version removed with success!', AlertVariant.success);
    } catch (error: any) {
      addNotification(error.message || 'Failed to remove the course version', AlertVariant.danger);
    } finally {
      endLoading();
    }
  };

  const renderPagination = (variant: string, isCompact: boolean) => (
    <Pagination
      isCompact={isCompact}
      itemCount={versions.length}
      page={page}
      perPage={perPage}
      onSetPage={(_evt, newPage) => setPage(newPage)}
      onPerPageSelect={(_evt, newPerPage, newPage) => {
        setPerPage(newPerPage);
        setPage(newPage);
      }}
      perPageOptions={[{ title: '5', value: 5 }, { title: '10', value: 10 }, { title: '20', value: 20 }]}
      variant={variant as PaginationVariant}
      titles={{ paginationAriaLabel: `${variant} pagination` }}
    />
  );

  const tableToolbar = (
    <Toolbar id="compact-toolbar">
      <ToolbarContent>
        <ToolbarItem>
          <Select
            id="select-example"
            aria-label="Select Input"
            toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
              <MenuToggle ref={toggleRef} onClick={() => setIsSelectOpen(!isSelectOpen)} isExpanded={isSelectOpen}>
                <FilterIcon /> Status
              </MenuToggle>
            )}
            isOpen={isSelectOpen}
            onOpenChange={(isOpen: boolean) => setIsSelectOpen(isOpen)}
            onSelect={() => setIsSelectOpen(!isSelectOpen)}
          >
            {[
              <SelectOption key="ACTIVE" value="ACTIVE">Active</SelectOption>,
              <SelectOption key="LEGACY" value="LEGACY">Legacy</SelectOption>,
              <SelectOption key="RETIRED" value="RETIRED">Retired</SelectOption>,
              <SelectOption key="COMING_SOON" value="COMING_SOON">Coming Soon</SelectOption>
            ]}
          </Select>
        </ToolbarItem>
        <ToolbarGroup>
          <ToolbarItem>
            {isAdmin && <Button variant="primary" onClick={onNewVersion}>New version</Button>}
          </ToolbarItem>
        </ToolbarGroup>
        <ToolbarItem variant="pagination">{renderPagination('top', true)}</ToolbarItem>
      </ToolbarContent>
    </Toolbar>
  );

  const renderCourseStatus = (labelText: string | undefined) => {
    switch (labelText) {
      case 'ACTIVE': return <Label color="green">{labelText}</Label>;
      case 'LEGACY': return <Label color="orange">{labelText}</Label>;
      case 'COMING_SOON': return <Label color="blue">{labelText}</Label>;
      case 'RETIRED': return <Label color="red">{labelText}</Label>;
      case 'RE_SKUED': return <Label color="blue">RE SKUED</Label>;
      default: return labelText || 'N/A';
    }
  };

  /** A course this one links to, SKU first and with its status alongside. */
  const linkedCourseDecorator = (linked: Course | undefined | null, value: string | number) => {
    if (!value) return <>N/A</>;
    return (
      <React.Fragment>
        {linked?.sku} - {linked?.name} {' '}
        {renderCourseStatus(linked?.status)}
      </React.Fragment>
    );
  }

  // Prevent rendering main content if course data hasn't loaded yet. A course being created
  // has no SKU until the user types one, so the guard only applies to the edit form.
  if (notFound) {
    return (
      <NotFoundState
        entityLabel="course"
        identifierLabel="SKU"
        identifier={sku}
        backTo={{ path: '/courses', label: 'Back to courses' }}
      />
    );
  }

  if (!isNew && !course.sku) return null;

  const changeLogTitle = course.sku;
  /**
   * Offered only once the record is stored: a form that has never been saved has no revision to
   * read, and the endpoint would answer 404 for the id it does not yet have.
   */
  const changeLogItems = course.id
    ? [{ key: 'change-log', label: 'Change log', onClick: () => setChangeLogOpen(true) }]
    : [];


  return (
    <EntityChangeLog
      entity="course"
      id={course.id}
      entityLabel="this course"
      title={changeLogTitle}
      isOpen={changeLogOpen}
      onClose={() => setChangeLogOpen(false)}
    >
      <DetailPageHeader
        title={isNew ? 'New Course' : course.sku}
        subtitle={course.name}
        actions={
          <ActionList>
            {/* Nothing here is writable without the administrator role, so the pair that writes
                is left out rather than shown permanently greyed. */}
            {isAdmin && (
              <React.Fragment>
                <ActionListItem>
                  <Button variant="secondary" id="cancel-button" isDisabled={!isDirty} onClick={onCancel}>Cancel</Button>
                </ActionListItem>
                <ActionListItem>
                  {/* Said out loud, rather than just greyed: a Save that goes dead the moment a
                      pencil is clicked is otherwise indistinguishable from one that is broken.
                      The reason is only attached while it applies - the rest of the time Save is
                      disabled for the obvious reason that there is nothing to save.

                      Wrapped in a span because a genuinely disabled button fires no mouse events,
                      so a tooltip hung directly on it would never open. Genuinely disabled and not
                      aria-disabled: the latter still lets the click through, which is the one thing
                      this must not do. */}
                  {hasOpenEditor ? (
                    <Tooltip content="Confirm or cancel the field you are editing before saving.">
                      <span>{saveButton}</span>
                    </Tooltip>
                  ) : saveButton}
                </ActionListItem>
              </React.Fragment>
            )}
            <ActionListItem>
              {/* Course removal is not released yet: the option stays visible but inactive. */}
              <PageActionsMenu
                items={changeLogItems}
                onRefresh={onRefresh}
                onRemove={onRemove}
                isRemoveDisabled
                removeConfirmationMessage={`Confirm removal of "${course.sku}"?`}
              />
            </ActionListItem>
          </ActionList>
        }
      >
        {course.isCertification && (
          <FlexItem>
            <Label color="orange">CERTIFICATION</Label>
          </FlexItem>
        )}
        {course.isBundle && (
          <FlexItem>
            <Label color="purple">BUNDLE</Label>
          </FlexItem>
        )}
      </DetailPageHeader>
      <PageSection hasBodyWrapper isWidthLimited>
        {!isAdmin && (
          <Alert
            isInline
            variant="info"
            title="You are viewing this course"
            style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}
          >
            Changing a course, or any of its versions, needs the administrator role. Everything
            here is readable because every class is scheduled against a course and one of its
            versions.
          </Alert>
        )}
        {Object.values(validated).includes('error') && (
          <FormAlert>
            <Alert variant="danger" title="Fill out all required fields before saving." aria-live="polite" isInline />
          </FormAlert>
        )}
        <DescriptionList columnModifier={{ default: '2Col' }}>
          <DescriptionListGroup>
            {/* The SKU is how every other record points at this course, so it is set once, on
                creation, and read only from then on — hence no pencil on the edit form. */}
            <InlineEdit
              label='SKU'
              type='TextInput'
              id="sku"
              onEditingChange={onFieldEditingChange}
              value={course.sku || ''}
              validated={validated.sku}
              errorText={VALIDATION_MESSAGE.sku}
              isGlobalEdition={isNew}
              isReadOnly={!isNew || !isAdmin}
              textInputProps={{ isRequired: true, placeholder: 'e.g. RH124', 'aria-label': 'SKU' }}
              onSave={(v) => v !== course.sku && updateCourse({ ...course, sku: v as string })}
            />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit
              label='Name'
              type='TextInput'
              id="name"
              onEditingChange={onFieldEditingChange}
              value={course.name || ''}
              validated={validated.name}
              errorText={VALIDATION_MESSAGE.name}
              isGlobalEdition={isNew}
              isReadOnly={!isAdmin}
              textInputProps={{ isRequired: true, 'aria-label': 'Course name' }}
              onSave={(v) => v !== course.name && updateCourse({ ...course, name: v as string })}
            />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit
              label='Status'
              type='Select'
              id="status"
              onEditingChange={onFieldEditingChange}
              value={course.status || ''}
              isGlobalEdition={isNew}
              isReadOnly={!isAdmin}
              options={Object.values(COURSE_STATUS).map(s => ({ value: s, label: s.replace(/_/g, ' ') }))}
              decorator={(v) => renderCourseStatus(v as string)}
              onSave={(v) => v !== course.status && updateCourse({ ...course, status: v as COURSE_STATUS })}
            />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit
              label='Curriculum'
              type='FilterableSelect'
              id="curriculum"
              onEditingChange={onFieldEditingChange}
              value={course.curriculum?.id ?? ''}
              isGlobalEdition={isNew}
              isReadOnly={!isAdmin}
              placeholder='Select a curriculum'
              decorator={() => course.curriculum?.name || 'N/A'}
              options={curricula.map(c => ({
                value: c.id,
                content: c.name,
                selected: c.id === course.curriculum?.id
              }))}
              onSave={(id) => updateCourse({ ...course, curriculum: curricula.find(c => c.id === id)! })}
            />
          </DescriptionListGroup>
          <DescriptionListGroup>
            <InlineEdit
              label='Is a certification'
              type='Checkbox'
              id="isCertification"
              onEditingChange={onFieldEditingChange}
              value={String(course.isCertification ?? false)}
              isGlobalEdition={isNew}
              isReadOnly={!isAdmin}
              decorator={(v) => (getBoolean(v) ? 'Yes' : 'No')}
              onSave={(v) => {
                const isCertification = getBoolean(v);
                if (isCertification === !!course.isCertification) return;
                // An exam certifies nothing and bundles nothing, so both links go with the flag
                updateCourse({
                  ...course,
                  isCertification,
                  certification: isCertification ? null : course.certification,
                  isBundle: isCertification ? false : course.isBundle,
                  bundleCourse: isCertification ? null : course.bundleCourse
                });
              }}
            />
          </DescriptionListGroup>
          {!course.isCertification && (
            <React.Fragment>
              <DescriptionListGroup>
                {/* Also the exam half of a bundled SKU - see 'Is a bundle' below. */}
                <InlineEdit
                  label='Certification'
                  type='FilterableSelect'
                  value={course.certification?.id ?? ''}
                  id="certification"
                  onEditingChange={onFieldEditingChange}
                  isGlobalEdition={isNew}
                  isReadOnly={!isAdmin}
                  validated={validated.certification}
                  errorText={VALIDATION_MESSAGE.certification}
                  placeholder='Select a certification'
                  onSave={(id) => updateCourse({ ...course, certification: certifications.find(i => i.id === id) ?? null })}
                  decorator={(v) => linkedCourseDecorator(course.certification, v)}
                  // A course cannot certify itself, so it is never offered as its own certification
                  options={certifications.filter(i => i.id !== course.id).map(i => ({
                    value: i.id as number,
                    content: `${i.sku} - ${i.name}`,
                    selected: course.certification ? (i.id === course.certification.id) : false
                  }))}
                />
              </DescriptionListGroup>
              <DescriptionListGroup>
                <InlineEdit
                  label='Is a bundle'
                  type='Checkbox'
                  id="isBundle"
                  onEditingChange={onFieldEditingChange}
                  value={String(course.isBundle ?? false)}
                  isGlobalEdition={isNew}
                  isReadOnly={!isAdmin}
                  decorator={(v) => (getBoolean(v) ? 'Yes' : 'No')}
                  onSave={(v) => {
                    const isBundle = getBoolean(v);
                    if (isBundle === !!course.isBundle) return;
                    // The course half means nothing once the SKU stops being a bundle
                    updateCourse({ ...course, isBundle, bundleCourse: isBundle ? course.bundleCourse : null });
                  }}
                />
              </DescriptionListGroup>
              {course.isBundle && (
                <DescriptionListGroup>
                  <InlineEdit
                    label='Bundled course'
                    type='FilterableSelect'
                    value={course.bundleCourse?.id ?? ''}
                    id="bundleCourse"
                    onEditingChange={onFieldEditingChange}
                    isGlobalEdition={isNew}
                    isReadOnly={!isAdmin}
                    validated={validated.bundleCourse}
                    errorText={VALIDATION_MESSAGE.bundleCourse}
                    placeholder='Select a course'
                    onSave={(id) => updateCourse({ ...course, bundleCourse: bundleCourses.find(c => c.id === id) ?? null })}
                    decorator={(v) => linkedCourseDecorator(course.bundleCourse, v)}
                    options={bundleCourses.map(c => ({
                      value: c.id as number,
                      content: `${c.sku} - ${c.name}`,
                      selected: course.bundleCourse ? (c.id === course.bundleCourse.id) : false
                    }))}
                  />
                </DescriptionListGroup>
              )}
            </React.Fragment>
          )}
        </DescriptionList>
      </PageSection>
      {/* Versions belong to a course that exists: there is nothing to list until it is saved */}
      {!isNew && (
        <PageSection hasBodyWrapper isWidthLimited>
          <PageSection hasBodyWrapper={false} isFilled>
            <SplitItem>
              <Title headingLevel="h1" size="lg">Course Versions</Title>
            </SplitItem>
            <Card>
              {tableToolbar}
              <Table variant="compact" aria-label="Course Versions Table">
                <Thead>
                  <Tr>
                    <Th>Extended SKU</Th>
                    <Th>Version</Th>
                    <Th>Status</Th>
                    <Th>Release Date</Th>
                    <Th>Duration (days)</Th>
                    <Th>Hours per day</Th>
                    <Th>Hardware Level</Th>
                    { ! course.isCertification && (
                      <Th>Certification?</Th>
                    )}
                    {isAdmin && <Th>Actions</Th>}
                  </Tr>
                </Thead>
                <Tbody>
                  {paginatedRows.map((row, rowIndex) => (
                    <Tr key={row.id || rowIndex}>
                      <Td dataLabel="Extended SKU">{row.extendedSku}</Td>
                      <Td dataLabel="Version">{row.version}</Td>
                      <Td dataLabel="Status">{renderCourseStatus(row.status)}</Td>
                      <Td dataLabel="Release Date">{ddMMyyyyFormat(row.releaseDate)}</Td>
                      <Td dataLabel="Duration (days)">{row.duration}</Td>
                      <Td dataLabel="Hours per day">{row.hoursDay}</Td>
                      <Td dataLabel="Hardware Level">{row.hardwareLevel}</Td>
                      { ! course.isCertification && (
                        // A bare boolean renders as nothing at all, so the column read empty
                        // whichever way the flag was set.
                        <Td dataLabel="Certification?">{row.certificationAvailable ? 'Yes' : 'No'}</Td>
                      )}
                      {isAdmin && (
                        <Td dataLabel="Actions" modifier="nowrap">
                          <ActionList>
                            <ActionListItem>
                              <Tooltip content="Edit">
                                <Button
                                  icon={<Icon size='lg'><PencilAltIcon /></Icon>}
                                  variant="plain"
                                  aria-label={`Edit version ${row.version}`}
                                  onClick={() => onEditVersion(row)}
                                />
                              </Tooltip>
                            </ActionListItem>
                            <ActionListItem>
                              <RemoveButton
                                confirmationMessage={`Confirm removal of version "${row.version}"?`}
                                onRemoveConfirm={() => onRemoveVersion(row.id)}
                              />
                            </ActionListItem>
                          </ActionList>
                        </Td>
                      )}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Card>
          </PageSection>
          {/* Never rendered for anybody who may not write one; the endpoints behind it say the
              same thing, so a token claiming otherwise gets no further. */}
          {isAdmin && (
            <CourseVersionModal
              isOpen={isVersionModalOpen}
              version={editedVersion}
              course={course}
              onClose={() => { setIsVersionModalOpen(false); setEditedVersion(null); }}
              onSave={onSaveVersion}
            />
          )}
        </PageSection>
      )}
      <Modal
        variant={ModalVariant.small}
        isOpen={blocker.state === 'blocked'}
        onClose={() => blocker.state === 'blocked' && blocker.reset()}
      >
        <ModalHeader>
          <Title
            headingLevel="h1"
            size="xl"
            style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
          >
            <Icon status="warning" size='xl'><ExclamationTriangleIcon /></Icon>
            Unsaved Changes
          </Title>
        </ModalHeader>
        <ModalBody>
          You have unsaved changes on this course. Are you sure you want to leave this page? Your changes will be lost.
        </ModalBody>
        <ModalFooter>
          <Button
            key="confirm"
            variant="danger"
            onClick={() => blocker.state === 'blocked' && blocker.proceed()}
          >
            Leave without saving
          </Button>
          <Button
            key="cancel"
            variant="link"
            onClick={() => blocker.state === 'blocked' && blocker.reset()}
          >
            Stay on page
          </Button>
        </ModalFooter>
      </Modal>
    </EntityChangeLog>
  );
};

export { CourseDetail };
